import type { RouteRecordRaw } from 'vue-router'
import { FLAGS } from '~/modules/rollout/constants'
import { CAPABILITIES } from './constants'

/**
 * Commission routes, for two workspaces.
 *
 * The officer's three screens are read-only. The administrator's console is the first thing
 * mounted under `/app/admin` — Phase 12 builds the rest of that workspace around it, which is
 * why the admin routes carry `capability: admin.access` rather than a commission-specific
 * slug: administration is a single boundary, and inventing a second one here would leave two
 * definitions of "is this an administrator" to drift.
 *
 * Paths come from `docs/v2/route-map.md`; a route exists there before it is built.
 */
/**
 * Phase 14 §2.1: the commission engine is behind `v2_commissions`, and a flagged-off feature is
 * **absent**. With the flag off these routes resolve to the not-found page and the sidebar
 * entries that lead to them are filtered out, so the menu never leads somewhere unavailable.
 * The API refuses them too — this is UX.
 */
const officer = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'affiliate' as const,
  capability: CAPABILITIES.commissionView,
  flag: FLAGS.commissions,
}

const admin = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'admin' as const,
  capability: CAPABILITIES.adminAccess,
  flag: FLAGS.commissions,
}

export const commissionRoutes: RouteRecordRaw[] = [
  {
    // Before `/commissions/:id`, so "payouts" is never read as an entry id.
    path: '/app/affiliate/commissions/payouts',
    name: 'affiliate-commission-payouts',
    component: () => import('./pages/AffiliatePayoutsPage.vue'),
    meta: { ...officer, title: 'affiliate.nav.payouts' },
  },
  {
    path: '/app/affiliate/commissions',
    name: 'affiliate-commissions',
    component: () => import('./pages/AffiliateCommissionPage.vue'),
    meta: { ...officer, title: 'affiliate.nav.commissions' },
  },
  {
    path: '/app/affiliate/commissions/:id',
    name: 'affiliate-commission-entry',
    component: () => import('./pages/CommissionEntryDetailPage.vue'),
    meta: { ...officer, title: 'affiliate.nav.commissions' },
  },

  {
    path: '/app/admin/commission-rules',
    name: 'admin-commission-rules',
    component: () => import('./pages/AdminRulesPage.vue'),
    meta: { ...admin, title: 'admin.nav.rules' },
  },
  {
    path: '/app/admin/commission-rules/new',
    name: 'admin-commission-rule-new',
    component: () => import('./pages/AdminRuleFormPage.vue'),
    meta: { ...admin, title: 'admin.nav.rules' },
  },
  {
    path: '/app/admin/commission-rules/:id',
    name: 'admin-commission-rule',
    component: () => import('./pages/AdminRuleFormPage.vue'),
    meta: { ...admin, title: 'admin.nav.rules' },
  },
  {
    path: '/app/admin/commissions/approve',
    name: 'admin-commission-approve',
    component: () => import('./pages/AdminApprovalQueuePage.vue'),
    meta: { ...admin, title: 'admin.nav.approvals' },
  },
  {
    path: '/app/admin/commissions/payouts',
    name: 'admin-commission-payouts',
    component: () => import('./pages/AdminPayoutsPage.vue'),
    meta: { ...admin, title: 'admin.nav.payouts' },
  },
  {
    path: '/app/admin/commissions/unmatched',
    name: 'admin-commission-unmatched',
    component: () => import('./pages/AdminUnmatchedPage.vue'),
    meta: { ...admin, title: 'admin.nav.unmatched' },
  },
  {
    path: '/app/admin/commissions',
    name: 'admin-commissions',
    component: () => import('./pages/AdminLedgerPage.vue'),
    meta: { ...admin, title: 'admin.nav.commissions' },
  },
  {
    path: '/app/admin/commissions/:id',
    name: 'admin-commission-entry',
    component: () => import('./pages/CommissionEntryDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.commissions' },
  },
]
